import { withCreds } from '../authService';

export const FRONTEND_DELIVERY_SCHEMA_VERSION = 'brightresolve.frontend-delivery.shadow.v11b';

const REF_PATTERN = /^[A-Za-z0-9_.:-]{1,128}$/;
const MAX_EVENTS = 32;
const TRANSPORT_TIMEOUT_MS = 5000;
const EVENT_TYPES = new Set([
  'response_received',
  'display_committed',
  'tts_requested',
  'audio_ready',
  'playback_requested',
  'playback_started',
  'playback_completed',
  'interrupted',
  'cancelled',
  'invalidated',
  'failed',
  'delivery_uncertain',
  'unavailable',
]);
const PLAYBACK_EVENTS = new Set([
  'tts_requested',
  'audio_ready',
  'playback_requested',
  'playback_started',
  'playback_completed',
  'interrupted',
  'cancelled',
  'failed',
]);

function eventRef() {
  if (globalThis.crypto && typeof globalThis.crypto.randomUUID === 'function') {
    return `delivery:${globalThis.crypto.randomUUID()}`;
  }
  return `delivery:${Date.now()}:${Math.random().toString(16).slice(2)}`;
}

export function validDeliveryEnvelope(value) {
  if (!value || typeof value !== 'object') return false;
  if (value.schema_version !== FRONTEND_DELIVERY_SCHEMA_VERSION) return false;
  if (typeof value.receipt !== 'string' || !value.receipt || value.receipt.length > 4096) return false;
  return [
    value.request_ref,
    value.conversation_ref,
    value.user_turn_ref,
    value.assistant_response_ref,
    value.generation_ref,
    value.semantic_unit_ref,
  ].every((item) => typeof item === 'string' && REF_PATTERN.test(item));
}

export function createFrontendDeliveryTracker(envelope, options = {}) {
  if (!validDeliveryEnvelope(envelope)) return null;
  let sequence = 0;
  let terminal = false;
  let playbackRef = '';
  let dispatchChain = Promise.resolve();
  const transport = options.transport || (async (payload) => {
    const controller = typeof AbortController === 'function' ? new AbortController() : null;
    const timeout = controller
      ? globalThis.setTimeout(() => controller.abort(), TRANSPORT_TIMEOUT_MS)
      : null;
    try {
      return await withCreds('/api/delivery/evidence', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        signal: controller?.signal,
        keepalive: true,
      });
    } finally {
      if (timeout != null) globalThis.clearTimeout(timeout);
    }
  });

  const emit = (eventType, details = {}) => {
    if (terminal || sequence >= MAX_EVENTS || !EVENT_TYPES.has(eventType)) return false;
    const suppliedPlaybackRef = String(details.playbackRef || playbackRef || '');
    if (PLAYBACK_EVENTS.has(eventType) && !REF_PATTERN.test(suppliedPlaybackRef)) return false;
    if (eventType === 'tts_requested') playbackRef = suppliedPlaybackRef;
    sequence += 1;
    const payload = {
      receipt: envelope.receipt,
      event_ref: eventRef(),
      event_type: eventType,
      sequence,
      request_ref: envelope.request_ref,
      conversation_ref: envelope.conversation_ref,
      user_turn_ref: envelope.user_turn_ref,
      assistant_response_ref: envelope.assistant_response_ref,
      generation_ref: envelope.generation_ref,
      playback_ref: suppliedPlaybackRef,
      semantic_unit_ref: envelope.semantic_unit_ref,
      client_revision: FRONTEND_DELIVERY_SCHEMA_VERSION,
    };
    if ([
      'playback_completed', 'interrupted', 'cancelled', 'invalidated', 'failed',
    ].includes(eventType)) terminal = true;
    // Preserve rendering/playback latency: acceptance is serialized for order,
    // but it is never awaited by the user-visible lifecycle owner.
    dispatchChain = dispatchChain
      .catch(() => undefined)
      .then(() => transport(payload))
      .catch(() => undefined);
    return true;
  };

  return {
    identity: Object.freeze({ ...envelope }),
    emit,
    invalidate: () => emit('invalidated'),
    isTerminal: () => terminal,
    whenIdle: () => dispatchChain,
  };
}

export function createPlaybackRef() {
  const suffix = globalThis.crypto && typeof globalThis.crypto.randomUUID === 'function'
    ? globalThis.crypto.randomUUID()
    : `${Date.now()}:${Math.random().toString(16).slice(2)}`;
  return `playback:${suffix}`;
}

export default createFrontendDeliveryTracker;
